/**
 * HeaderDetailPage - Reusable header component for detail pages
 * Based on PageActivity.tsx and PageWorkPlan.tsx detail page headers
 */

// react
import { ReactNode } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink } from 'react-router-dom';

// components
import { RowFinancialSummary } from '../RowFinancialSummary/RowFinancialSummary';
import type { FxRate } from '../../../utils/fxRate';
import { TruncatedText } from '../TruncatedText/TruncatedText';

// mui
import { alpha, Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Edit as IconEdit } from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';

// design system
import { radius, shadows, spacing, typography } from '../../../utils/designSystem';

// data
import { COLORS, GRADIENTS } from '../../../data/colors';

// Types for color variants
type ColorVariant = 'activity' | 'workPlan' | 'contract' | 'condition' | 'financial' | 'payment';

// Status color mapping
export const getStatusColor = (status: string) => {
	const normalizedStatus = status.toUpperCase().replace(/[_\s]/g, '');
	switch (normalizedStatus) {
		case 'COMPLETED':
		case 'VERIFIED':
		case 'APPROVED':
			return { bg: alpha(COLORS.success.main, 0.15), text: COLORS.success.dark };
		case 'INPROGRESS':
		case 'ACTIVE':
		case 'READY':
			return { bg: alpha(COLORS.info.main, 0.15), text: COLORS.info.dark };
		case 'SUBMITTED':
		case 'PENDING':
		case 'UNDERREVIEW':
			return { bg: alpha(COLORS.warning.main, 0.15), text: COLORS.warning.dark };
		case 'DELAYED':
		case 'OVERDUE':
			return { bg: alpha(COLORS.warning.main, 0.15), text: COLORS.warning.dark };
		case 'CANCELLED':
		case 'REJECTED':
			return { bg: alpha(COLORS.error.main, 0.15), text: COLORS.error.dark };
		case 'REDEEMED':
			return { bg: alpha(COLORS.success.main, 0.15), text: COLORS.success.dark };
		case 'DRAFT':
		case 'PLANNED':
		default:
			return { bg: alpha(COLORS.success.main, 0.15), text: COLORS.success.dark };
	}
};

// Status label mapping (backend status → display label)
export const getStatusLabel = (status: string, t: (key: string) => string) => {
	const normalizedStatus = status.toUpperCase().replace(/[_\s]/g, '');
	switch (normalizedStatus) {
		case 'PENDING':
			return t('notStarted');
		case 'SUBMITTED':
			return t('underReview');
		case 'READY':
			return t('condition.readyForPayment');
		case 'VERIFIED':
			return t('conditionsVerified');
		case 'REDEEMED':
			return t('paid');
		case 'CANCELLED':
			return t('cancelled');
		case 'REJECTED':
			return t('rejected');
		default:
			return status.replace(/_/g, ' ');
	}
};

interface Breadcrumb {
	label: string;
	to: string;
}

interface Stat {
	label: string;
	value: ReactNode;
	/** Optional: a smaller sub-line under the value (e.g. a local-currency equivalent) */
	secondary?: ReactNode;
	/** Optional: make the stat clickable */
	to?: string;
}

// Financial summary types
interface BaseFinancialData {
	currency: string;
	planned: number;
	committed: number;
	spent: number;
	currencyLocal?: string | null;
	/**
	 * Canonical FX rate. Drives the local-equivalent caption and the rate-source
	 * tooltip via `<LocalEquivalent>` inside `RowFinancialSummary`.
	 */
	fx?: FxRate | null;
	/**
	 * Project reporting currency. When the headline `currency` is the
	 * project's local currency, the caption flips and shows a reporting
	 * equivalent instead of a local one (so the same row never shows the
	 * same currency twice).
	 */
	currencyReporting?: string | null;
}

interface SubActivityAllocation {
	allocated: number;
	unallocated: number;
}

interface ContractFinancialData {
	currency: string;
	contractValue: number;
	commitmentAmount?: number | null;
	paid: number;
	/**
	 * Per-loan breakdown of the contract's paid total. When length > 1 the
	 * `RowFinancialSummary` Paid stat's tooltip renders a compact
	 * loan-by-loan table instead of the generic blurb.
	 */
	paidByLoan?: { loanId: number; loanName?: string | null; amount: number }[];
	/**
	 * Legacy V1-era paid amount, shown as a separate progress segment and
	 * stat so it is not conflated with V2 paid.
	 */
	paidV1?: number;
	/** Pending vouchers (created but not yet redeemed; excludes CANCELLED). */
	pending?: number;
	/**
	 * Additional contract amounts in currencies other than `currency`.
	 * Rendered as a small caption so a multi-currency contract is not
	 * collapsed into a single figure under a single label.
	 */
	additionalAmounts?: { currency: string; amount: number }[];
	/**
	 * The parent work plan's currency, when it differs from `currency`.
	 * Surfaced as a sub-label under the contract value so users see the
	 * relationship between the contract figure and the planning currency
	 * without one being silently rendered as the other.
	 */
	workPlanCurrency?: string;
	currencyLocal?: string | null;
	/**
	 * Canonical FX rate. Drives the local-equivalent caption and the rate-source
	 * tooltip via `<LocalEquivalent>` inside `RowFinancialSummary`.
	 */
	fx?: FxRate | null;
	/**
	 * Project reporting currency. Used to flip the contract row caption
	 * to a reporting equivalent when the contract is denominated in the
	 * project's local currency.
	 */
	currencyReporting?: string | null;
}

export interface FinancialSummaryProps {
	/** Financial data for work plan/activity variant */
	data?: BaseFinancialData;
	/** Financial data for contract variant */
	contractData?: ContractFinancialData;
	/** Sub-activity allocation data (only for activity variant) */
	subActivityAllocation?: SubActivityAllocation;
}

interface HeaderDetailPageProps {
	/** The icon component to display */
	icon: SvgIconComponent;
	/** Breadcrumb navigation */
	breadcrumb: Breadcrumb;
	/** The main title (entity name) */
	title: string;
	/** Status of the entity */
	status?: string;
	/** Color variant for the header styling */
	variant?: ColorVariant;
	/** Stats to display below the header */
	stats?: Stat[];
	/** Handler for edit button click */
	onEdit?: () => void;
	/** Custom edit button label */
	editLabel?: string;
	/** Custom action button (replaces edit button) */
	customAction?: ReactNode;
	/** Badge to display after the title (e.g., ChipInfo) */
	titleBadge?: ReactNode;
	/** Additional chips to display after the status chip */
	extraChips?: ReactNode;
	/** Loading state - shows skeleton when true */
	loading?: boolean;
	/** Number of action button skeletons to show when loading (default: 2) */
	loadingActions?: number;
	/** Whether to show financial summary skeleton when loading (default: false) */
	showFinancialSummary?: boolean;
	/** Optional financial summary to display below the header */
	financialSummary?: FinancialSummaryProps;
}

export const HeaderDetailPage = ({
	icon: Icon,
	breadcrumb,
	title,
	status,
	variant = 'activity',
	stats,
	onEdit,
	editLabel = 'Edit',
	customAction,
	titleBadge,
	extraChips,
	loading = false,
	loadingActions = 2,
	showFinancialSummary = false,
	financialSummary,
}: HeaderDetailPageProps) => {
	const { t } = useTranslation();
	// Get colors based on variant
	const colors = GRADIENTS[variant] || GRADIENTS.activity;
	const statusColors = status ? getStatusColor(status) : null;

	// Loading skeleton - show real icon and breadcrumb, skeleton only for data-dependent parts
	if (loading) {
		return (
			<Paper
				elevation={0}
				sx={{
					bgcolor: alpha(colors.border, 0.04),
					borderRadius: 0,
					mb: 0,
					borderBottom: '1px solid',
					borderColor: 'divider',
					position: 'relative',
					overflow: 'hidden',
				}}>
				{/* Header Bar - Show real icon and breadcrumb */}
				<Box sx={{ px: { xs: spacing.sm, sm: spacing.md }, py: spacing.md, position: 'relative' }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Stack direction="row" alignItems="center" spacing={{ xs: spacing.sm, md: spacing.md }}>
							{/* Real Icon Box - matches loaded state */}
							<Box
								sx={{
									alignItems: 'center',
									background: `linear-gradient(135deg, ${colors.icon}, ${colors.border})`,
									borderRadius: radius.md,
									boxShadow: shadows.icon(colors.icon),
									display: 'flex',
									flexShrink: 0,
									height: { xs: 44, sm: 52 },
									justifyContent: 'center',
									width: { xs: 44, sm: 52 },
								}}>
								<Icon sx={{ color: '#fff', fontSize: { xs: 22, sm: 26 } }} />
							</Box>

							{/* Real Breadcrumb + Title Skeleton */}
							<Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
								{/* Real breadcrumb link - matches loaded state */}
								<Typography
									component={RouterLink}
									to={breadcrumb.to}
									sx={{
										...typography.h3,
										color: '#374151',
										fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
										textDecoration: 'none',
										'&:hover': { textDecoration: 'underline' },
									}}>
									{breadcrumb.label}
								</Typography>
								{/* Real slash - matches loaded state */}
								<Typography
									sx={{
										color: '#9ca3af',
										fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
										fontWeight: 300,
									}}>
									/
								</Typography>
								{/* Title skeleton */}
								<Skeleton
									variant="text"
									width={200}
									height={32}
									sx={{ bgcolor: alpha(colors.icon, 0.18) }}
								/>
								{/* Status chip skeleton - fully rounded to match Chip */}
								<Skeleton
									variant="rounded"
									width={90}
									height={26}
									sx={{ borderRadius: 13, bgcolor: alpha(colors.icon, 0.12) }}
								/>
							</Stack>
						</Stack>
						{/* Action Buttons Skeleton - uses loadingActions prop */}
						{loadingActions > 0 && (
							<Stack direction="row" spacing={1}>
								{Array.from({ length: loadingActions }).map((_, index) => (
									<Skeleton
										key={index}
										variant="rounded"
										width={index === loadingActions - 1 ? 140 : 80}
										height={36}
										sx={{
											borderRadius: radius.md,
											bgcolor: alpha(colors.icon, index === loadingActions - 1 ? 0.15 : 0.12),
										}}
									/>
								))}
							</Stack>
						)}
					</Stack>
				</Box>

				{/* Stats Row Skeleton */}
				<Box
					sx={{
						px: { xs: spacing.sm, sm: spacing.md },
						py: spacing.sm,
						borderTop: '1px solid',
						borderColor: 'divider',
						bgcolor: alpha('#f9fafb', 0.5),
					}}>
					<Stack
						direction="row"
						spacing={{ xs: 2, md: 3 }}
						sx={{ flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
						{[1, 2, 3, 4, 5].map((i) => (
							<Box key={i}>
								<Skeleton
									variant="text"
									width={80}
									height={22}
									sx={{ bgcolor: alpha(colors.icon, 0.1), mb: 0.25 }}
								/>
								<Skeleton
									variant="text"
									width={i === 1 || i === 2 ? 150 : 100}
									height={28}
									sx={{ bgcolor: alpha(colors.icon, 0.15) }}
								/>
							</Box>
						))}
					</Stack>
				</Box>

				{/* Financial Summary Row Skeleton */}
				{(financialSummary || showFinancialSummary) && (
					<Box
						sx={{
							px: { xs: spacing.sm, sm: spacing.md },
							py: spacing.sm,
							borderTop: '1px solid',
							borderColor: 'divider',
						}}>
						<Stack
							direction="row"
							alignItems="center"
							spacing={{ xs: 2, md: 4 }}
							sx={{ flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
							{/* Progress bar skeleton */}
							<Box sx={{ minWidth: 280 }}>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between"
									sx={{ mb: 0.5 }}>
									<Skeleton
										variant="text"
										width={100}
										height={22}
										sx={{ bgcolor: alpha(colors.icon, 0.1) }}
									/>
									<Skeleton
										variant="text"
										width={40}
										height={26}
										sx={{ bgcolor: alpha(colors.icon, 0.15) }}
									/>
								</Stack>
								<Skeleton
									variant="rounded"
									width="100%"
									height={10}
									sx={{ borderRadius: 0.5, bgcolor: alpha(colors.icon, 0.12) }}
								/>
							</Box>

							{/* Stat items skeleton */}
							{[1, 2, 3, 4].map((i) => (
								<Box key={i}>
									<Skeleton
										variant="text"
										width={80}
										height={22}
										sx={{ bgcolor: alpha(colors.icon, 0.1), mb: 0.25 }}
									/>
									<Skeleton
										variant="text"
										width={100}
										height={28}
										sx={{ bgcolor: alpha(colors.icon, 0.15) }}
									/>
								</Box>
							))}
						</Stack>
					</Box>
				)}
			</Paper>
		);
	}

	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: alpha(colors.border, 0.04),
				borderRadius: 0,
				mb: 0,
				borderBottom: '1px solid',
				borderColor: 'divider',
				position: 'relative',
				overflow: 'hidden',
			}}>
			{/* Header Bar - SIMPLIFIED: no gradient background */}
			<Box sx={{ px: { xs: spacing.sm, sm: spacing.md }, py: spacing.md, position: 'relative' }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack direction="row" alignItems="center" spacing={{ xs: spacing.sm, md: spacing.md }}>
						{/* Gradient Icon Box - matches HeaderListingPage */}
						<Box
							sx={{
								alignItems: 'center',
								background: `linear-gradient(135deg, ${colors.icon}, ${colors.border})`,
								borderRadius: radius.md,
								boxShadow: shadows.icon(colors.icon),
								display: 'flex',
								flexShrink: 0,
								height: { xs: 44, sm: 52 },
								justifyContent: 'center',
								width: { xs: 44, sm: 52 },
							}}>
							<Icon sx={{ color: '#fff', fontSize: { xs: 22, sm: 26 } }} />
						</Box>

						{/* Breadcrumb + Title + Status - LARGER, HIGH CONTRAST */}
						<Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
							<Typography
								component={RouterLink}
								to={breadcrumb.to}
								sx={{
									...typography.h3,
									color: '#374151', // Dark gray
									fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
									textDecoration: 'none',
									'&:hover': { textDecoration: 'underline' },
								}}>
								{breadcrumb.label}
							</Typography>
							<Typography
								sx={{
									color: '#9ca3af',
									fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
									fontWeight: 300,
								}}>
								/
							</Typography>
							<Typography
								sx={{
									...typography.h1,
									color: '#111827', // Near black
									fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.75rem' },
									fontWeight: 700,
								}}>
								{title}
							</Typography>
							{titleBadge}
							{status && statusColors && (
								<Chip
									label={getStatusLabel(status, t)}
									size="small"
									sx={{
										bgcolor: statusColors.bg,
										color: statusColors.text,
										fontSize: { xs: '0.875rem', sm: '1rem' },
										fontWeight: 600,
										height: { xs: 26, sm: 28 },
										px: 1,
									}}
								/>
							)}
							{extraChips}
						</Stack>
					</Stack>

					{/* Action Button */}
					{customAction ? (
						customAction
					) : onEdit ? (
						<Button
							startIcon={<IconEdit />}
							variant="outlined"
							onClick={onEdit}
							size="medium"
							sx={{
								borderColor: colors.icon,
								borderRadius: radius.sm,
								color: colors.icon,
								fontSize: { xs: '1rem', sm: '1.25rem' },
								fontWeight: 600,
								px: { xs: 2, sm: 2.5 },
								py: 1,
								'&:hover': {
									bgcolor: alpha(colors.icon, 0.08),
									borderColor: colors.icon,
								},
							}}>
							{editLabel}
						</Button>
					) : null}
				</Stack>
			</Box>

			{/* Stats Row - IMPROVED spacing and contrast */}
			{stats && stats.length > 0 && (
				<Box
					sx={{
						px: { xs: spacing.sm, sm: spacing.md },
						py: spacing.sm,
						borderTop: '1px solid',
						borderColor: 'divider',
						bgcolor: alpha('#f9fafb', 0.5),
					}}>
					<Stack
						direction="row"
						spacing={{ xs: 2, md: 3 }}
						sx={{ flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
						{stats.map((stat, index) => {
							const content = (
								<Box key={index} sx={{ minWidth: 0, maxWidth: 240 }}>
									<Typography
										sx={{
											...typography.labelSmall,
											color: '#6b7280', // Medium gray
											fontSize: { xs: '1.125rem', sm: '1.25rem' },
											mb: 0.25,
											fontWeight: 600,
											letterSpacing: '0.03em',
											textTransform: 'uppercase',
										}}>
										{stat.label}
									</Typography>
									{typeof stat.value === 'string' || typeof stat.value === 'number' ? (
										<TruncatedText
											className="stat-value"
											component="div"
											text={String(stat.value)}
											sx={{
												...typography.bodyBold,
												color: '#111827', // Near black
												fontSize: { xs: '1.3125rem', sm: '1.5rem' },
												fontWeight: 600,
											}}
										/>
									) : (
										<Typography
											className="stat-value"
											component="div"
											sx={{
												...typography.bodyBold,
												color: '#111827', // Near black
												fontSize: { xs: '1.3125rem', sm: '1.5rem' },
												fontWeight: 600,
											}}>
											{stat.value}
										</Typography>
									)}
									{stat.secondary &&
										(typeof stat.secondary === 'string' ? (
											<TruncatedText
												component="div"
												text={stat.secondary}
												sx={{
													color: '#6b7280',
													fontSize: { xs: '1.0625rem', sm: '1.15rem' },
													fontWeight: 500,
													mt: 0.25,
												}}
											/>
										) : (
											<Typography
												component="div"
												sx={{
													color: '#6b7280',
													fontSize: { xs: '1.0625rem', sm: '1.15rem' },
													fontWeight: 500,
													mt: 0.25,
												}}>
												{stat.secondary}
											</Typography>
										))}
								</Box>
							);

							if (stat.to) {
								return (
									<Box
										key={index}
										component={RouterLink}
										to={stat.to}
										sx={{
											textDecoration: 'none',
											cursor: 'pointer',
											'&:hover': { '& .stat-value': { textDecoration: 'underline' } },
										}}>
										{content}
									</Box>
								);
							}

							return content;
						})}
					</Stack>
				</Box>
			)}

			{/* Financial Summary Row */}
			{financialSummary &&
				(variant === 'workPlan' || variant === 'activity' || variant === 'contract') && (
					<RowFinancialSummary
						variant={variant}
						data={financialSummary.data}
						contractData={financialSummary.contractData}
						subActivityAllocation={financialSummary.subActivityAllocation}
						compact
					/>
				)}
		</Paper>
	);
};
